import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
	DocumentTextIcon,
	ChartBarIcon,
	UserGroupIcon,
	ArrowTrendingUpIcon,
	PhotoIcon,
	ChatBubbleLeftRightIcon,
	BuildingOfficeIcon,
	PresentationChartBarIcon,
	NewspaperIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../api/auth';

const AdminDashboard = () => {
	const { user, isAdmin, isGestor } = useAuth();
	const [statsData, setStatsData] = useState({});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadDashboardStats();
	}, []);

	const loadDashboardStats = async () => {
		try {
			setLoading(true);
			const data = await authApi.getDashboardStats();
			// Si no hay token, data será null (esperado cuando no está logueado)
			if (data === null) {
				setStatsData({});
				return;
			}
			setStatsData(data);
		} catch (error) {
			// Solo mostrar error si no es por falta de token (que es esperado)
			if (error.message !== 'No hay token de autenticación') {
				console.error('Error cargando estadísticas del dashboard:', error);
			}
			// En caso de error, usar valores por defecto
			setStatsData({});
		} finally {
			setLoading(false);
		}
	};

	const stats = [
		{
			name: 'Formularios DGII',
			key: 'formularios_dgii',
			value: loading ? '...' : (statsData.formularios_dgii || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: DocumentTextIcon,
			link: '/admin/dgii',
			roles: ['admin', 'gestor', 'cliente']
		},
		{
			name: 'Admin DGII',
			key: 'admin_dgii',
			value: loading ? '...' : (statsData.admin_dgii || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: ChartBarIcon,
			link: '/admin/dgii/admin',
			roles: ['admin', 'gestor']
		},
		{
			name: 'Banners',
			key: 'banners',
			value: loading ? '...' : (statsData.banners || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: PhotoIcon,
			link: '/admin/banners',
			roles: ['admin']
		},
		{
			name: 'Testimonios',
			key: 'testimonios',
			value: loading ? '...' : (statsData.testimonios || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: ChatBubbleLeftRightIcon,
			link: '/admin/testimonials',
			roles: ['admin']
		},
		{
			name: 'Partners',
			key: 'partners',
			value: loading ? '...' : (statsData.partners || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: BuildingOfficeIcon,
			link: '/admin/partners',
			roles: ['admin']
		},
		{
			name: 'Estadísticas',
			key: 'estadisticas',
			value: loading ? '...' : (statsData.estadisticas || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: PresentationChartBarIcon,
			link: '/admin/statistics',
			roles: ['admin']
		},
		{
			name: 'Blog',
			key: 'blog',
			value: loading ? '...' : (statsData.blog || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: NewspaperIcon,
			link: '/admin/blog',
			roles: ['admin']
		},
		{
			name: 'Usuarios',
			key: 'usuarios',
			value: loading ? '...' : (statsData.usuarios || 0).toString(),
			change: '+0%',
			changeType: 'positive',
			icon: UserGroupIcon,
			link: '/admin/users',
			roles: ['admin']
		}
	];

	const filteredStats = stats.filter(stat => {
		if (stat.roles.includes('admin') && isAdmin()) return true;
		if (stat.roles.includes('gestor') && isGestor()) return true;
		if (stat.roles.includes('cliente')) return true;
		return false;
	});

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8 pt-2 lg:pt-0">
				<h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
					Bienvenido, {user?.first_name || user?.username || 'Usuario'}
				</h1>
				<p className="mt-2 text-sm text-gray-600">
					Panel de administración del sistema
				</p>
			</div>

			{/* Stats Grid */}
			<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
				{filteredStats.map((stat) => {
					const Icon = stat.icon;
					return (
						<Link
							key={stat.name}
							to={stat.link}
							className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
						>
							<div className="p-5">
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<Icon className="h-6 w-6" style={{ color: '#12284C' }} />
									</div>
									<div className="ml-5 w-0 flex-1">
										<dl>
											<dt className="text-sm font-medium text-gray-500 truncate">
												{stat.name}
											</dt>
											<dd className="flex items-baseline">
												<div className="text-2xl font-semibold text-gray-900">
													{stat.value}
												</div>
												<div
													className={`ml-2 flex items-baseline text-sm font-semibold ${
														stat.changeType === 'positive'
															? 'text-green-600'
															: 'text-red-600'
													}`}
												>
													<ArrowTrendingUpIcon
														className={`self-center flex-shrink-0 h-5 w-5 ${
															stat.changeType === 'positive'
																? 'text-green-500'
																: 'text-red-500'
														}`}
														aria-hidden="true"
													/>
													<span className="sr-only">
														{stat.changeType === 'positive'
															? 'Increased'
															: 'Decreased'}{' '}
														by
													</span>
													{stat.change}
												</div>
											</dd>
										</dl>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-5 py-3">
								<div className="text-sm">
									<span className="font-medium hover:opacity-80" style={{ color: '#12284C' }}>
										Ver detalles →
									</span>
								</div>
							</div>
						</Link>
					);
				})}
			</div>

			{/* Quick Actions */}
			<div className="bg-white shadow rounded-lg">
				<div className="px-6 py-4 border-b border-gray-200">
					<h2 className="text-lg font-medium text-gray-900">Accesos Rápidos</h2>
				</div>
				<div className="p-6">
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{filteredStats.map((stat) => {
							const Icon = stat.icon;
							return (
								<Link
									key={stat.name}
									to={stat.link}
									className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
								>
									<Icon className="h-6 w-6 mr-3" style={{ color: '#12284C' }} />
									<span className="text-sm font-medium text-gray-900">
										{stat.name}
									</span>
								</Link>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminDashboard;

